import { EffectCallback } from 'react';
// import logger, { TAG_NAMES } from 'utils/Logger';

export const SoundMap = {
  BELL: 'sounds/bell.mp3',
  NOTIFICATION: 'sounds/notification.mp3',
  ALERT: 'sounds/alert.mp3',
  // Add more sounds as required
};

export const SoundMode = {
  currentMode: 0,
  allModes: [
    {
      mode: 0,
      title: 'Web Audio API',
      description: 'Full control over playback, volume, etc.',
      problem: 'Limited by browser capabilities and autoplay restrictions'
    }
  ]
};

export enum SoundEndedStatus {
  stopped = 'stopped',
  playing = 'playing'
}

export type SoundEnded = {
  soundName: string;
  status: SoundEndedStatus;
};

export type SoundFinishCallback = (data: SoundEnded) => void;
export type CanBeResumedCallback = () => void;

const soundEventEmitter = new EventTarget();

export const addSoundFinishListener = (callback: SoundFinishCallback): ReturnType<EffectCallback> => {
  const handler = (event: Event) => {
    callback((event as CustomEvent).detail);
  };
  soundEventEmitter.addEventListener('audioPlayerDidFinishPlaying', handler);
  return () => soundEventEmitter.removeEventListener('audioPlayerDidFinishPlaying', handler);
};

export const addAudioPlayerCanBeResumedListener = (callback: CanBeResumedCallback): ReturnType<EffectCallback> => {
  soundEventEmitter.addEventListener('audioPlayerCanBeResumed', callback);
  return () => soundEventEmitter.removeEventListener('audioPlayerCanBeResumed', callback);
};

type IPlay = keyof typeof SoundMap;

const audioInstances: Record<string, HTMLAudioElement> = {};

/**
 * Play the given sound from SoundMap
 */
export function play(key: IPlay, callback?: Function) {
  const soundPath = SoundMap[key];
  if (!soundPath) {
    // logger(TAG_NAMES.SOUND, `Sound path for key "${key}" not found.`, {
    //   isBreadcrumb: true,
    //   isLogToFile: true,
    // });
    return;
  }

  if (audioInstances[key]) {
    audioInstances[key].pause();
    audioInstances[key].currentTime = 0;
  }

  const audio = new Audio(soundPath);
  audioInstances[key] = audio;

  audio.play()
    .then(() => {
      callback?.();
    })
    .catch((error) => {
    //   logger(TAG_NAMES.SOUND, `Sound Callback: Play`, {
    //     isBreadcrumb: true,
    //     isLogToFile: true,
    //     data: { error },
    //   });
      callback?.(error);
    });

  audio.addEventListener('ended', () => {
    soundEventEmitter.dispatchEvent(new CustomEvent('audioPlayerDidFinishPlaying', {
      detail: { soundName: key, status: SoundEndedStatus.stopped }
    }));
  });
}

export function changeTone(key: IPlay, newVolume: number, duration: number) {
  const audio = audioInstances[key];
  if (!audio) return;

  audio.volume = newVolume;
  // Fade the volume over `duration` seconds
  const step = (newVolume - audio.volume) / (duration * 100);
  const interval = setInterval(() => {
    if (Math.abs(audio.volume - newVolume) < 0.01) {
      clearInterval(interval);
      audio.volume = newVolume;
    } else {
      audio.volume += step;
    }
  }, 10);
}

export function release(key: IPlay) {
  const audio = audioInstances[key];
  if (audio) {
    audio.pause();
    delete audioInstances[key];
  }
}

export function playTone(key: IPlay) {
  play(key);
}

export function loadTones() {
  // Preload audio files to reduce initial playback latency
  Object.keys(SoundMap).forEach((key) => {
    const soundPath = SoundMap[key as IPlay];
    const audio = new Audio(soundPath);
    audioInstances[key as IPlay] = audio;
  });
}

export function playToneQueue(key: IPlay) {
  const audioQueue = audioInstances[key] || new Audio(SoundMap[key]);
  if (!audioQueue) return;

  audioQueue.addEventListener('ended', () => {
    soundEventEmitter.dispatchEvent(new CustomEvent('audioPlayerDidFinishPlaying', {
      detail: { soundName: key, status: SoundEndedStatus.stopped }
    }));
  });

  audioQueue.play().catch((error) => {
    // logger(TAG_NAMES.SOUND, `Queue Playback Error`, {
    //   isBreadcrumb: true,
    //   isLogToFile: true,
    //   data: { error },
    // });
  });
}

export function stop(key: IPlay) {
//   logger(TAG_NAMES.SOUND, `Stop ${key as string} sound`, { isLogToFile: true });
  const audio = audioInstances[key];
  if (audio) {
    audio.pause();
    audio.currentTime = 0;
  }
}

export function audioSessionChangeObserver() {
  // Example: Detect if the user switches tabs, or focus is lost
  window.addEventListener('blur', () => {
    Object.values(audioInstances).forEach((audio) => {
      audio.pause();
    });
    soundEventEmitter.dispatchEvent(new Event('audioPlayerCanBeResumed'));
  });

  window.addEventListener('focus', () => {
    soundEventEmitter.dispatchEvent(new Event('audioPlayerCanBeResumed'));
  });

  return () => {
    window.removeEventListener('blur', () => {});
    window.removeEventListener('focus', () => {});
  };
}
